import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useSetRecoilState } from 'recoil'
import { chartOptionAtom, keyForElementIdAndType } from 'recoil/settings'
import {
  ChartOptionElement,
  useGetChartOptionQuery,
  useGetReportDetailsQuery,
  useGetReportFavoriteDetailsQuery,
} from 'gqlTypes'
import pick from 'lodash/pick'
import first from 'lodash/first'
import Navbar from './Navbar'
import { Box, LinearProgress } from '@mui/material'
import ReportHeader from './ReportHeader'
import ReportContents from './ReportContents'
import ReportFavoriteElements from './ReportFavoriteElements'
import { ChartOptionsContextProvider } from 'contexts/ChartOptionsContext'
import ReportNavigation from './ReportNavigation'

const ReportFavorites: React.FC = () => {
  const { id, sectionId } = useParams()

  const setGlobalOptions = useSetRecoilState(
    chartOptionAtom(
      keyForElementIdAndType({ id: id || '', type: ChartOptionElement.Report })
    )
  )
  const { data: chartOptionData } = useGetChartOptionQuery({
    variables: { id: id || '', type: ChartOptionElement.Report },
  })

  const { data, refetch, loading } = useGetReportFavoriteDetailsQuery({
    variables: { id: id ?? '' },
  })

  const {
    data: allData,
    refetch: allRefetch,
    loading: allLoading,
  } = useGetReportDetailsQuery({
    variables: { id: id ?? '' },
  })

  const handleUpdateFavorites = () => {
    refetch()
    allRefetch()
  }

  useEffect(() => {
    if (chartOptionData) {
      if (chartOptionData.chartOption) {
        setGlobalOptions(
          pick(chartOptionData.chartOption, [
            'cohort',
            'subgroup',
            'pastResults',
            'config',
          ])
        )
      } else {
        setGlobalOptions({
          cohort: first(data?.reportWithFavorites?.cohorts),
          subgroup: first(data?.reportWithFavorites?.subgroups),
          pastResults: true,
        })
      }
    }
  }, [chartOptionData, data])

  if (loading || allLoading) {
    return (
      <>
        <Navbar />
        <LinearProgress />
      </>
    )
  }

  const report = data?.reportWithFavorites
  const allDataReport = allData?.report

  if (!report || !allDataReport) {
    return <h1>Report not found</h1>
  }

  return (
    <>
      <Navbar />
      <ReportHeader favorites report={report} sectionId={sectionId} />
      <Box sx={{ display: 'flex', maxWidth: '100%' }}>
        <ReportContents favorites report={report} sectionId={sectionId} />
        {sectionId ? (
          <Box
            sx={{
              p: 4,
              width: 'calc(100% - 300px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <ReportNavigation favorites report={report} sectionId={sectionId} />
            <ReportFavoriteElements
              report={report}
              sectionId={sectionId}
              onUpdateFavorites={handleUpdateFavorites}
            />
            <ReportNavigation favorites report={report} sectionId={sectionId} />
          </Box>
        ) : null}
      </Box>
    </>
  )
}

const WrappedReportFavorites: React.FC = (props) => {
  const { id } = useParams()
  return (
    <ChartOptionsContextProvider id={id || ''} type={ChartOptionElement.Report}>
      <ReportFavorites {...props} />
    </ChartOptionsContextProvider>
  )
}

export default WrappedReportFavorites
